import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Github, Twitter, MessageSquare, Mail, Star, ExternalLink } from "lucide-react";
import Logo from "./Logo";
import { api } from "../lib/api";

// Phase 20 — Footer enhancement. Adds: live GitHub stars badge (with
// graceful fallback when the API is rate-limited or offline), social
// row, "stay in the loop" CTA, and a more spacious column layout.
//
// Newsletter form is intentionally non-functional for now — it nudges
// users toward email + GitHub stars without promising a real list.

const GITHUB_REPO = "ErnestKostevich/horizon-genesis";

function useGithubStars() {
  const [stars, setStars] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`https://api.github.com/repos/${GITHUB_REPO}`, { headers: { Accept: "application/vnd.github+json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled && j) setStars(j.stargazers_count ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return stars;
}

export default function Footer() {
  const stars = useGithubStars();

  return (
    <footer className="relative border-t border-border1 mt-12" data-testid="site-footer">
      {/* Top callout strip — newsletter + stars + social */}
      <div className="border-b border-border1 bg-surface/30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent mb-3">/stay in the loop</div>
            <div className="font-display text-2xl md:text-3xl tracking-tight text-text1 max-w-md leading-tight">
              Release notes, plugin spotlights, no spam.
            </div>
            <p className="font-body text-sm text-text2 mt-2 max-w-md leading-relaxed">
              Drop your email — you'll hear from us when Horizon ships a real
              installer, opens a new sandbox backend, or a plugin author makes
              their first sale.
            </p>
            <NewsletterForm />
          </div>

          {/* Phase 20.5 — right column items-center'd to kill the empty
              gap that appeared when the newsletter (left) was taller
              than the GitHub button + social row (right). Buttons get
              a solid surface bg so they read on both themes. */}
          <div className="flex flex-col gap-4 sm:items-end">
            <a
              href={`https://github.com/${GITHUB_REPO}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 px-4 h-12 rounded-lg border-2 border-border1 bg-surface hover:border-amber1/60 hover:bg-surface2 hover:-translate-y-0.5 transition-all shadow-sm hover:shadow-md"
            >
              <Github size={18} strokeWidth={1.7} className="text-text1" />
              <span className="font-mono text-[12px] uppercase tracking-wider text-text1 font-semibold">Star on GitHub</span>
              <span className="flex items-center gap-1.5 font-mono text-[12px] text-amber1 border-l-2 border-border1 pl-3 ml-1 group-hover:text-amber2 font-semibold">
                <Star size={13} strokeWidth={1.8} fill="currentColor" />
                {stars !== null ? stars.toLocaleString() : "—"}
              </span>
            </a>

            <div className="flex items-center gap-2 sm:justify-end">
              <SocialLink href={`https://github.com/${GITHUB_REPO}`} icon={Github} label="GitHub" />
              <SocialLink href="https://x.com/Ernest3360" icon={Twitter} label="Twitter / X" />
              <SocialLink href="https://discord.gg/eEq4PCPMEm" icon={MessageSquare} label="Discord" />
              <SocialLink href="mailto:ernest2011kostevich@gmail.com" icon={Mail} label="Email" />
            </div>

            {/* Phase 20.5 — fill the right column with a meaningful status
                pill so it doesn't feel half-empty next to the newsletter
                copy. "All systems normal" was buried in the bottom row
                before — promoting it up gives the right side a third
                element and balances the visual weight. */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-400/10 border border-emerald-400/30">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
              </span>
              <span className="font-mono text-[10px] uppercase tracking-wider text-emerald-400">all systems normal</span>
            </div>
          </div>
        </div>
      </div>

      {/* Link columns */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-6 gap-8 md:gap-10 font-mono text-xs">
        <div className="col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <Logo size={18} className="text-amber1 logo-pulse" />
            <span className="text-text1 font-display font-bold tracking-tight">HORIZON</span>
          </div>
          <p className="text-text2 font-body text-sm leading-relaxed max-w-sm">
            A desktop AI agent you own. Local-first, bring-your-own-keys,
            extensible with a moderated marketplace of plugins and workflows.
          </p>
        </div>

        <FooterCol title="Product" links={[
          { label: "Features",     href: "/#features"  },
          { label: "How it works", href: "/#how"       },
          { label: "Download",     to:   "/download"   },
          { label: "Pricing",      to:   "/pricing"    },
          { label: "Architecture", to:   "/about"      },
        ]} />

        <FooterCol title="Marketplace" links={[
          { label: "Browse plugins", to: "/browse"               },
          { label: "Workflows",      to: "/browse?type=workflow" },
          { label: "Publish",        to: "/publish"              },
          { label: "Creators",       to: "/creators"             },
        ]} />

        <FooterCol title="Docs" links={[
          { label: "Quick start",         to: "/docs/quick-start"  },
          { label: "Plugin SDK",          to: "/docs/plugin-sdk"   },
          { label: "Publishing rules",    to: "/docs/publishing"   },
          { label: "Monetization (70/30)", to: "/docs/monetization" },
        ]} />

        <FooterCol title="Legal" links={[
          { label: "Privacy",  to: "/privacy"  },
          { label: "Terms",    to: "/terms"    },
          { label: "Contact",  to: "/contact"  },
          { label: "Email",    href: "mailto:ernest2011kostevich@gmail.com", external: true },
        ]} />
      </div>

      <div className="border-t border-border1 py-4">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-wider text-text2">
          <span>© {new Date().getFullYear()} Horizon · Built by Ernest Kostevich</span>
          <span className="flex items-center gap-2">
            <span>v0.0.3 · horizon.genesis</span>
            <span className="hidden sm:inline">·</span>
            <span className="text-accent">BSL 1.1</span>
          </span>
        </div>
      </div>
    </footer>
  );
}

// Phase 20.4 — wired newsletter form. Posts to /api/newsletter on the
// FastAPI backend; falls back to a mailto: link if the endpoint isn't
// available (so the form is never a dead end). Stores the submission
// locally too so a returning user sees "already subscribed".
function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | ok | dupe | err
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    try {
      const saved = localStorage.getItem("hzn_newsletter_email");
      if (saved) { setEmail(saved); setState("dupe"); }
    } catch {}
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setState("err"); setErrMsg("That doesn't look like an email."); return;
    }
    setState("sending"); setErrMsg("");
    try {
      await api.post("/api/newsletter/subscribe", { email });
      try { localStorage.setItem("hzn_newsletter_email", email); } catch {}
      setState("ok");
    } catch (ex) {
      // Backend not deployed yet → fall back to mailto so the user still
      // has a way to reach Ernest. Use window.location to open mail client.
      const fallback = `mailto:ernest2011kostevich@gmail.com?subject=Newsletter%20signup&body=${encodeURIComponent("Add " + email + " to release notes.")}`;
      window.location.href = fallback;
      setState("err");
      setErrMsg("Backend's offline — opened your mail client as a fallback.");
    }
  };

  if (state === "ok" || state === "dupe") {
    return (
      <div className="mt-5 flex items-center gap-3 max-w-md p-3.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 font-mono text-[12px]">
        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
        {state === "ok" ? `Subscribed — we'll email ${email} on release.` : `Already subscribed as ${email}.`}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-5 flex flex-col sm:flex-row gap-2 max-w-md">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => { setEmail(e.target.value); if (state !== "idle") setState("idle"); }}
        placeholder="you@example.com"
        disabled={state === "sending"}
        className="flex-1 bg-bg/80 border border-border1 rounded-lg px-3 h-10 font-mono text-[12px] text-text1 placeholder:text-text2/60 focus:outline-none focus:border-accent/60 focus:ring-2 focus:ring-accent/20 transition-colors disabled:opacity-60"
        aria-label="Email address"
      />
      <button
        type="submit"
        disabled={state === "sending"}
        className="font-mono text-[12px] uppercase tracking-wider px-5 h-10 bg-amber1 text-black hover:bg-amber2 rounded-lg transition-colors disabled:opacity-60"
      >
        {state === "sending" ? "…" : "Subscribe"}
      </button>
      {state === "err" && errMsg && (
        <div className="basis-full font-mono text-[11px] text-rose-400 mt-1">{errMsg}</div>
      )}
    </form>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="text-text2 uppercase mb-3 tracking-wider">{title}</div>
      <ul className="space-y-1.5">
        {links.map((l) => (
          <li key={l.label}>
            {l.to ? (
              <Link to={l.to} className="text-text1 hover:text-amber1 transition-colors inline-flex items-center gap-1">
                {l.label}
              </Link>
            ) : (
              <a
                href={l.href}
                {...(l.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="text-text1 hover:text-amber1 transition-colors inline-flex items-center gap-1"
              >
                {l.label}
                {l.external && <ExternalLink size={10} strokeWidth={1.8} className="opacity-50" />}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SocialLink({ href, icon: Icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      className="w-10 h-10 rounded-lg border-2 border-border1 bg-surface flex items-center justify-center text-text1 hover:text-accent hover:bg-surface2 hover:border-accent/50 hover:-translate-y-0.5 transition-all shadow-sm"
    >
      <Icon size={16} strokeWidth={1.8} />
    </a>
  );
}
